import { create } from 'zustand';
import config, { ROUTES } from '../config';
import {
  updateNavigation,
  replacePathPlaceholders,
} from '../helpers/utilities';
import moment from 'moment';
import { checkAuthentication, getRoom, exitRoom } from '../lib/api';
import useSessionStore from './session';

const useAppStore = create((set, get) => ({
  roomSid: null,
  room: null,
  user: {
    sid: null,
    firstName: null,
    lastName: null,
    token: null,
    accountSid: null,
    identity: null,
  },
  timeOffset: null,
  proctorUserSid: null,
  checkinDrawerWidth: 300,
  isDrawerOpen: false,
  drawerWidth: 0,
  drawerMode: 'testing',
  drawerCloseConfirmation: null, // a function that is called before the drawer is closed...
  allowSounds: true,
  loginScreenMessage: null,
  proctors: {},
  pageLoader: { enabled: false, text: null },
  authenticate: async (token) => {
    const returnObj = { successful: false };

    const authCheck = await checkAuthentication(token);

    if (!authCheck.successful) {
      return returnObj;
    } else {
      // if the user is already setup, then we can just return...
      if (get()?.user?.sid) {
        returnObj.successful = true;
        return returnObj;
      }

      // authentication was successful, setup the session store...

      // Put validated params into storage
      sessionStorage.setItem(config.vtc.authentication.localStorageToken, token);

      const userData = {
        sid: authCheck.data.proctorAccountUserSid,
        firstName: authCheck.data.firstName,
        lastName: authCheck.data.lastName,
        email: authCheck.data.email,
        accountSid: authCheck.data.proctorAccountSid,
        token: token,
        identity: authCheck.data.proctorAccountUserSid,
        entered: moment.utc().toISOString(),
      };

      set({ user: userData });
      returnObj.successful = true;
    }
    return returnObj;
  },

  setRoomSid: async (sid) => {
    const response = { successful: false };

    // first fetch the room...
    const room = await getRoom(sid);

    if (room.successful) {
      // ensure the room belongs to the account that has

      if (room.data.proctorAccountSid !== get().user.accountSid) {
        response.message = 'Room does not belong to your account';
        return response;
      }

      // ensure they have access to the room...
      if (!room.data.isShared && get().user.sid !== room.data.ownerSid) {
        response.message = 'You do not have access to this room';
        return response;
      }

      // the sid is not part of the response for room, add it to make sure it is in the system...
      room.data.sid = sid;

      set({ roomSid: sid, room: room.data });
      sessionStorage.setItem(config.vtc.authentication.localStorageRoomSid, sid);

      response.successful = true;
    } else {
      response.message =
        'There was an error while attempting to load this room. Please refresh the page.';
    }

    return response;
  },

  setUser: async (userData) => {
    set({ user: userData });
  },

  showPageLoader: (message) => {
    set({ pageLoader: { enabled: true, text: message } });
  },

  clearPageLoader: () => {
    set({ pageLoader: { enabled: false, text: null } });
  },

  removeProctor: (proctorUserSid) =>
    set((state) => {
      const proctors = { ...state.proctors };

      delete proctors[proctorUserSid];

      return {
        proctors,
      };
    }),

  addProctor: (proctorUserSid, data) =>
    set((state) => {
      const proctors = { ...state.proctors };

      // But do not add yourself...
      if (get()?.user?.sid !== proctorUserSid) {
        proctors[proctorUserSid] = data;
      }

      return {
        proctors,
      };
    }),

  setLoginScreenMessage: async (message) => {
    let messageObj = { message: null, type: 'error' };

    if (!message) {
      messageObj = null;
    } else if (typeof message === 'string') {
      messageObj.message = message;
    } else if (typeof message === 'object' && message !== null) {
      if ('message' in message) {
        messageObj.message = message.message;
        if (message.type) {
          messageObj.type = message.type;
        }
      } else {
        messageObj = null;
      }
    } else {
      messageObj = null;
    }
    set({ loginScreenMessage: messageObj });
  },

  setAllowSounds: async (value) => {
    set({ allowSounds: value });
  },

  exitRoom: async () => {
    get().showPageLoader('Exiting room...');
    const accountSid = get().user.accountSid;
    const roomSid = get().roomSid;
    const userSid = get().user.sid;
    set({ roomSid: null, room: null });
    await exitRoom(accountSid, roomSid, userSid);
    updateNavigation(replacePathPlaceholders(ROUTES.ROOMS));
  },

  logout: async (options = {}) => {
    get().showPageLoader('Logging out...');
    // Store these so they can be used for the exitRoom call...
    const accountSid = get().user.accountSid;
    const roomSid = get().roomSid;
    const userSid = get().user.sid;

    // clear from the room first to ensure the event emitter is not triggered after exiting...
    set({ roomSid: null, room: null });
    await exitRoom(accountSid, roomSid, userSid);
    // now clear the user information. This has to com after exitRoom b/c the user token is needed for the api call...
    set({ user: null });
    // Clear all values from session Storage...
    sessionStorage.removeItem(config.vtc.authentication.localStorageToken);
    sessionStorage.removeItem(config.vtc.authentication.localStorageRoomSid);

    if (options?.loginScreenMessage) {
      get().setLoginScreenMessage(options.loginScreenMessage);
    } else {
      get().setLoginScreenMessage({
        message: 'You were successfully logged out.',
        type: 'success',
      });
    }

    // kick back to login...
    updateNavigation(replacePathPlaceholders(ROUTES.LOGIN));
  },

  setDrawerCloseConfirmation: async (fn) => {
    set({ drawerCloseConfirmation: fn });
  },

  openDrawer: (drawerMode, sessionSid) => {
    if (get().isDrawerOpen && drawerMode !== get().drawerMode) {
      // if there is a confirmation function registered, make sure we call it first...
      if (get().drawerCloseConfirmation) {
        const wasClosed = get().closeDrawer(false);
        if (!wasClosed) {
          return false;
        }
      } else {
        // there is not confirmation so just close the drawer
        //get().closeDrawer(false);
      }
    }
    let navRoute, navOptions;
    let drawerWidth = '0px';

    if (drawerMode === 'checkin') {
      drawerWidth = config.vtc.modes.checkin.drawerWidth;
      navRoute = ROUTES.CHECKIN;
      navOptions = { roomSid: get().roomSid };
    } else if (drawerMode === 'focus') {
      drawerWidth = config.vtc.modes.focus.drawerWidth;
      // set the focused session sid
      useSessionStore.getState().setFocusedSession(sessionSid);
      navRoute = ROUTES.SESSION_FOCUS;
      navOptions = { roomSid: get().roomSid, sessionSid: sessionSid };
    } else if (drawerMode === 'settings') {
      drawerWidth = config.vtc.modes.settings.drawerWidth;
      navRoute = ROUTES.SETTINGS;
      navOptions = { roomSid: get().roomSid };
    } else if (drawerMode === 'options') {
      drawerWidth = config.vtc.modes.options.drawerWidth;
      navRoute = ROUTES.OPTIONS;
      navOptions = { roomSid: get().roomSid };
    }

    set({
      drawerWidth: drawerWidth,
      isDrawerOpen: true,
      drawerMode: drawerMode,
    });

    updateNavigation(replacePathPlaceholders(navRoute, navOptions));
  },
  closeDrawer: (triggerUrlUpdate = true) => {
    // if there is a confirmation function registered, make sure we call it first...
    if (get().drawerCloseConfirmation) {
      // there is, so fire it and return false...
      get().drawerCloseConfirmation();
      return false;
    }

    if (get().drawerMode === 'focus') {
      // since the drawer closed, clear the focused session...
      useSessionStore.getState().clearFocusedSession();
    }

    set({
      isDrawerOpen: false,
      drawerMode: '',
      drawerWidth: '0px',
    });

    if (triggerUrlUpdate) {
      updateNavigation(
        replacePathPlaceholders(ROUTES.ROOM, { roomSid: get().roomSid })
      );
    }
  },
  /*
  setSessionDimensions: (width, height) => {
    set({ sessionDimensions: { width, height } });
  },

  setTestingAreaDimensions: (width, height) => {
    set({ testingAreaDimensions: { width, height } });
    //get().calculateLayoutSizing();
  },

  setRoomDimensions: (width, height) => {
    if (width && height) {
      set({ roomDimensions: { width, height } });
    }
  },
  */
}));

export default useAppStore;
